// pages/LoanApplications.tsx
import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  PlusIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import { loanService, LoanApplication } from '@/services/loanService'
import LoanApplicationModal from '@/components/LoanApplicationModal'
import LoanApplicationApprovalModal from '@/components/LoanApplicationApprovalModal'
import toast from 'react-hot-toast'
import { BanknoteIcon } from 'lucide-react'

const LoanApplications: React.FC = () => {
  const { user } = useAuth()
  const [showLoanApplicationModal, setShowLoanApplicationModal] = useState(false)
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState<LoanApplication | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  // Fetch loan applications
  const { data: applicationsData, isLoading: loadingApplications, refetch: refetchApplications } = useQuery({
    queryKey: ['loanApplications'],
    queryFn: () => loanService.getLoanApplications(),
    staleTime: 5 * 60 * 1000,
  })

  const loanApplications: LoanApplication[] = applicationsData?.data?.loanApplications || []

  // Filter applications based on search and status
  const filteredApplications = loanApplications.filter(app => {
    const matchesSearch =
      app.employee.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.employee.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.loanType.name.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === 'all' || app.status === statusFilter

    return matchesSearch && matchesStatus
  })

  const handleRefresh = async () => {
    try {
      await refetchApplications()
      toast.success('Applications refreshed successfully')
    } catch (error) {
      toast.error('Failed to refresh applications')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
      case 'manager_approved':
      case 'hr_approved':
      case 'finance_approved':
        return 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400'
      case 'approved':
      case 'disbursed':
        return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
      case 'rejected':
        return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
      default:
        return 'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const canTakeAction = (application: LoanApplication) => {
    const { role } = user || {}
    const { status } = application

    if (role === 'admin') return true

    switch (status) {
      case 'pending':
        return role === 'manager'
      case 'manager_approved':
        return role === 'hr_manager'
      case 'hr_approved':
        return role === 'finance_manager'
      case 'finance_approved':
        return ['hr_manager', 'finance_manager'].includes(role || '')
      case 'approved':
        return ['admin', 'finance_manager'].includes(role || '')
      default:
        return false
    }
  }

  const canCreateApplication = user?.role === 'employee'

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 rounded-lg bg-indigo-600 flex items-center justify-center">
            <DocumentTextIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Loan Applications</h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              Manage and review employee loan applications
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium inline-flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
          >
            <ArrowPathIcon className="h-3 w-3" />
            Refresh
          </button>
          {canCreateApplication && (
            <button
              onClick={() => setShowLoanApplicationModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium inline-flex items-center gap-2 px-3 py-2 transition-all duration-200 hover:shadow-md"
            >
              <PlusIcon className="h-3 w-3" />
              New Application
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search by employee or loan type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="manager_approved">Manager Approved</option>
              <option value="hr_approved">HR Approved</option>
              <option value="finance_approved">Finance Approved</option>
              <option value="approved">Approved</option>
              <option value="disbursed">Disbursed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <DocumentTextIcon className="h-4 w-4" />
            Loan Applications ({filteredApplications.length})
          </h2>
        </div>

        {loadingApplications ? (
          <div className="flex justify-center items-center py-8">
            <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="text-center py-8">
            <div className="h-12 w-12 mx-auto bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mb-3">
              <DocumentTextIcon className="text-gray-400 h-5 w-5" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              No Applications Found
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-xs mb-4">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your filters"
                : "No loan applications found"}
            </p>
            {canCreateApplication && (
              <button
                onClick={() => setShowLoanApplicationModal(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium inline-flex items-center gap-2 px-3 py-2 transition-all duration-200 hover:shadow-md"
              >
                <PlusIcon className="h-3 w-3" />
                New Application
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  {[
                    "Employee",
                    "Loan Type",
                    "Amount",
                    "Term",
                    "Status",
                    "Applied On",
                    "Actions",
                  ].map((col) => (
                    <th key={col}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map((application) => (
                  <tr key={application.id}>
                    <td>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white text-xs">
                          {application.employee.firstName} {application.employee.lastName}
                        </p>
                        <p className="text-gray-500 dark:text-gray-400 text-xs">
                          {application.employee.employeeNumber}
                        </p>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="h-6 w-6 rounded flex items-center justify-center bg-indigo-50 dark:bg-indigo-900/20">
                          <BanknoteIcon className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white text-xs">{application.loanType.name}</p>
                          <p className="text-gray-500 dark:text-gray-400 text-xs">{application.loanType.interestRate}% interest</p>
                        </div>
                      </div>
                    </td>
                    <td className="font-medium text-gray-900 dark:text-white text-xs">
                      {formatCurrency(application.requestedAmount)}
                    </td>
                    <td className="text-gray-900 dark:text-white text-xs">
                      {application.loanTermMonths} months
                    </td>
                    <td>
                      <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${getStatusColor(application.status)}`}>
                        {application.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="text-gray-600 dark:text-gray-400 text-xs">
                      {formatDate(application.createdAt)}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setSelectedApplication(application)
                            setShowApprovalModal(true)
                          }}
                          disabled={!canTakeAction(application)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                            canTakeAction(application)
                              ? 'text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                              : 'text-gray-300 cursor-not-allowed'
                          }`}
                          title={canTakeAction(application) ? "Review Application" : "No action available"}
                        >
                          <CheckCircleIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showLoanApplicationModal && (
        <LoanApplicationModal
          onClose={() => setShowLoanApplicationModal(false)}
          onSuccess={() => {
            refetchApplications()
          }}
        />
      )}

      {showApprovalModal && selectedApplication && (
        <LoanApplicationApprovalModal
          application={selectedApplication}
          onClose={() => {
            setShowApprovalModal(false)
            setSelectedApplication(null)
          }}
          onSuccess={() => {
            refetchApplications()
          }}
        />
      )}
    </div>
  )
}

export default LoanApplications